/**
 * KCIS Clinical Deep Dive™ — premium tiered investigatory experience.
 *
 * Three phases:
 *   welcome     → emotional onboarding (royal purple / serif / storytelling)
 *   assessment  → tier-aware sectioned runner (autosaves between sections)
 *   complete    → preliminary Clinical Intelligence Summary + booking CTAs
 *
 * Touches: GET /api/deep-dive/questionnaire, POST /api/deep-dive/start,
 * PATCH /api/deep-dive/by-token/:t/answers, POST .../complete, GET .../summary.
 * Never touches the legacy assessment engine, wellness gateway, intelligence
 * libraries, reasoning routes, or monetization layer logic.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Mail,
  CalendarCheck2,
  Microscope,
  Stethoscope,
  Loader2,
  ShieldCheck,
  HeartPulse,
  Compass,
} from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { deepDiveApi } from "@/lib/deepDiveApi";


/* ─────────────────── Palette tokens ───────────────────
 * Royal Purple   #3D2266 / #5C1E8A  (primary)
 * Deep Plum BG   #150B27            (concierge dark)
 * White          #FFFFFF
 * Silver         #C0C5CE / #E8E2F1
 * Hint of Green  #2D6B50            (success / next step accent)
 */
const TIER_LABELS = {
  intro:    { label: "KCIS Clinical Deep Dive™ — Intro",    blurb: "Understanding" },
  standard: { label: "KCIS Clinical Deep Dive™ — Standard", blurb: "Understanding · Direction" },
  premium:  { label: "KCIS Clinical Deep Dive™ — Premium",  blurb: "Understanding · Direction · Concierge Review" },
};

const BOOKING_URL      = process.env.REACT_APP_BOOKING_URL      || "https://book.squareup.com/appointments/";
const MICROSCOPIC_URL  = process.env.REACT_APP_MICROSCOPIC_URL  || BOOKING_URL;
const CONSULTATION_URL = process.env.REACT_APP_CONSULTATION_URL || BOOKING_URL;


export default function ClinicalDeepDive({ tier, purchaseToken }) {
  const [phase, setPhase] = useState("welcome");           // welcome | assessment | complete
  const [qn, setQn]       = useState(null);
  const [submission, setSubmission] = useState(null);
  const [completionData, setCompletionData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await deepDiveApi.questionnaire(tier);
        if (!cancelled) setQn(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load questionnaire.");
      }
    })();
    return () => { cancelled = true; };
  }, [tier]);

  const begin = async () => {
    if (!purchaseToken) { setPhase("assessment"); return; }
    try {
      const r = await deepDiveApi.start(purchaseToken);
      setSubmission(r.data.submission);
      setPhase("assessment");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start your Deep Dive.");
    }
  };

  if (error) return <ErrorShell msg={error} />;
  if (!qn)   return <LoadingShell />;

  if (phase === "welcome")  return <Welcome tier={tier} qn={qn} onBegin={begin} />;
  if (phase === "complete") return <Complete tier={tier} purchaseToken={purchaseToken} completionData={completionData} />;
  return (
    <AssessmentRunner
      tier={tier} qn={qn} purchaseToken={purchaseToken} submission={submission}
      onComplete={(data) => { setCompletionData(data); setPhase("complete"); }}
    />
  );
}


/* ─────────────────── Welcome (Premium Onboarding) ─────────────────── */
function Welcome({ tier, qn, onBegin }) {
  const t = TIER_LABELS[tier] || TIER_LABELS.intro;
  return (
    <Shell testid="deep-dive-welcome" tier={tier}>
      {/* Hero — concierge dark with luminous silver accent */}
      <section className="relative overflow-hidden bg-[#150B27] text-white" data-testid="dd-hero">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-purple-700/30 blur-3xl" />
          <div className="absolute top-20 right-0 w-[28rem] h-[28rem] rounded-full bg-fuchsia-700/20 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-emerald-700/15 blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 pt-20 pb-24 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-[10px] uppercase tracking-[0.32em] text-purple-200 font-semibold backdrop-blur-sm" data-testid="dd-tier-pill">
            <Sparkles className="w-3 h-3" /> {t.blurb}
          </span>

          <h1
            data-testid="dd-headline"
            className="mt-8 font-display text-[2.6rem] sm:text-6xl leading-[1.02] tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, 'Times New Roman', serif", fontWeight: 500 }}
          >
            We see you.
          </h1>
          <p className="mt-3 text-purple-200 text-xs uppercase tracking-[0.32em]">You&rsquo;re not alone.</p>

          <p className="mt-10 text-lg sm:text-xl text-slate-100 max-w-2xl mx-auto leading-relaxed" data-testid="dd-welcome-1">
            Thank you for trusting us with the next layer of your story.
          </p>
          <p className="mt-5 text-base text-slate-300 max-w-2xl mx-auto leading-relaxed" data-testid="dd-welcome-2">
            We already have your Wellness Assessment responses. <strong className="text-white">This experience allows us to investigate your concerns more deeply.</strong>
          </p>
          <p className="mt-5 text-base text-slate-400 max-w-2xl mx-auto leading-relaxed italic" data-testid="dd-welcome-3">
            There are no right or wrong answers. Simply tell us your story — we are listening.
          </p>

          <div className="mt-12 inline-flex items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-purple-200">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Private &amp; Secure</span>
            <span className="w-px h-3 bg-white/20" />
            <span className="inline-flex items-center gap-2"><HeartPulse className="w-3.5 h-3.5" /> Clinically Reviewed</span>
            <span className="hidden sm:inline-flex w-px h-3 bg-white/20" />
            <span className="hidden sm:inline-flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> Investigative, Not Diagnostic</span>
          </div>
        </div>
      </section>

      {/* The journey arc */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <div className="text-center mb-10">
            <span className="text-[10px] uppercase tracking-[0.32em] text-purple-900 font-semibold">The KCIS Journey</span>
            <h2 className="mt-3 font-display text-3xl text-slate-900 leading-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
              You are entering the second of four stages.
            </h2>
          </div>
          <ol className="grid sm:grid-cols-4 gap-3" data-testid="dd-journey-arc">
            {[
              { tag: "Screening",     name: "Wellness Gateway",      state: "done" },
              { tag: "Investigation", name: "Clinical Deep Dive™",   state: "active" },
              { tag: "Confirmation",  name: "Clinical Consultation", state: "next" },
              { tag: "Implementation",name: "Protocol",              state: "future" },
            ].map((s, i) => {
              const tone = s.state === "active"
                ? "bg-[#150B27] text-white border-[#150B27]"
                : s.state === "done"
                  ? "bg-emerald-50 text-emerald-900 border-emerald-200"
                  : "bg-white text-slate-700 border-slate-200";
              return (
                <li key={s.name} className={`rounded-2xl border ${tone} px-4 py-5`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Stage {i + 1}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.22em] font-semibold">{s.tag}</div>
                  <div className="mt-1 font-display text-base leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{s.name}</div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* What we'll explore + Begin CTA */}
      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.32em] text-purple-900 font-semibold mb-3">
          What we&rsquo;ll explore together
        </div>
        <h3 className="font-display text-2xl text-slate-900 mb-7" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          This is the next layer.
        </h3>
        <ul className="grid sm:grid-cols-2 gap-3" data-testid="dd-overview-list">
          {[
            "Your health story & timeline",
            "Hair & scalp history",
            "Symptom progression and severity",
            "Triggers and previous treatments",
            "Stress, sleep, and lifestyle",
            "Nutrition, supplements, medications",
            "Family history & environmental influences",
            "Goals and desired outcomes",
          ].map((line) => (
            <li
              key={line}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 hover:border-purple-300 transition"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#5C1E8A] shrink-0" />
              {line}
            </li>
          ))}
        </ul>

        <div className="mt-12 rounded-2xl border border-purple-200/70 bg-gradient-to-br from-purple-50/40 via-white to-white p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-purple-900 font-semibold">Your {tier} experience</div>
              <div className="mt-1 font-display text-2xl text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {qn.total_sections} sections · {qn.total_questions} reflective questions
              </div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">Save as you go. Return any time. Your story is yours.</p>
            </div>
            <Button
              size="lg"
              onClick={onBegin}
              className="rounded-full bg-[#150B27] hover:bg-[#241241] text-white text-base px-8 py-6 shadow-lg shadow-purple-900/20"
              data-testid="dd-begin-btn"
            >
              Begin My Clinical Deep Dive™
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        <p className="mt-10 text-center text-xs text-slate-500 italic">
          &ldquo;We kiss the mind, teach the wisdom, and make the mission better.&rdquo;
        </p>
      </main>
    </Shell>
  );
}


/* ─────────────────── Assessment Runner ─────────────────── */
function AssessmentRunner({ tier, qn, purchaseToken, onComplete }) {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [answers, setAnswers]       = useState({});
  const [saving, setSaving]         = useState(false);
  const [finishing, setFinishing]   = useState(false);

  const section = qn.sections[sectionIdx];
  const isLast = sectionIdx === qn.sections.length - 1;

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [sectionIdx]);

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));
  const sectionAnswers = useMemo(() => {
    const out = {};
    for (const q of section.questions) if (answers[q.id] !== undefined) out[q.id] = answers[q.id];
    return out;
  }, [answers, section]);

  const persistAndAdvance = async (advance) => {
    if (purchaseToken) {
      setSaving(true);
      try {
        await deepDiveApi.saveAnswers(purchaseToken, sectionAnswers, section.key);
      } catch { /* tolerate */ }
      finally { setSaving(false); }
    }
    if (advance === "next" && !isLast) setSectionIdx((i) => i + 1);
    if (advance === "back" && sectionIdx > 0) setSectionIdx((i) => i - 1);
  };

  const finish = async () => {
    setFinishing(true);
    let completionData = null;
    if (purchaseToken) {
      try {
        const r = await deepDiveApi.complete(purchaseToken, answers);
        completionData = r?.data || null;
      } catch { /* tolerate */ }
    }
    setFinishing(false);
    onComplete(completionData);
  };

  const t = TIER_LABELS[tier] || TIER_LABELS.intro;
  const pct = ((sectionIdx + 1) / qn.sections.length) * 100;

  return (
    <Shell testid="deep-dive-runner" tier={tier}>
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[10px] uppercase tracking-[0.22em] text-purple-900 font-semibold">{t.label}</span>
            <span className="text-slate-500" data-testid="dd-progress">Section {sectionIdx + 1} of {qn.sections.length}</span>
          </div>
          <div className="h-1 bg-slate-100 rounded-full mt-2 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-[#3D2266] via-[#5C1E8A] to-[#2D6B50] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <div className="text-[10px] uppercase tracking-[0.32em] text-purple-900 font-semibold mb-2">{section.key.replace(/_/g, " ")}</div>
        <h1
          className="font-display text-4xl text-slate-900 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
          data-testid="dd-section-title"
        >
          {section.title}
        </h1>
        {section.intro && <p className="mt-3 text-base text-slate-700 leading-relaxed italic">{section.intro}</p>}

        <div className="mt-10 space-y-7" data-testid="dd-question-list">
          {section.questions.map((q) => (
            <Question key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
          ))}
        </div>

        <div className="mt-12 flex items-center justify-between">
          <Button
            variant="ghost" onClick={() => persistAndAdvance("back")}
            disabled={sectionIdx === 0 || saving || finishing}
            data-testid="dd-back-btn" className="text-slate-600"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {isLast ? (
            <Button onClick={finish} disabled={finishing || saving}
              className="bg-[#2D6B50] hover:bg-[#22573F] text-white rounded-full px-8 py-6"
              data-testid="dd-finish-btn"
            >
              {finishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Complete My Deep Dive
            </Button>
          ) : (
            <Button onClick={() => persistAndAdvance("next")} disabled={saving}
              className="bg-[#150B27] hover:bg-[#241241] text-white rounded-full px-8 py-6"
              data-testid="dd-next-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Next Section <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </Shell>
  );
}

function Question({ q, value, onChange }) {
  return (
    <div data-testid={`dd-q-${q.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-purple-200 transition">
      <label
        className="block text-lg text-slate-900 leading-relaxed"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
      >
        {q.prompt}
      </label>
      {q.type === "textarea" && (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={q.placeholder || "Take your time…"}
          className="mt-4 min-h-[120px] text-base"
          data-testid={`dd-input-${q.id}`}
        />
      )}
      {q.type === "text" && (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-4" data-testid={`dd-input-${q.id}`} />
      )}
      {q.type === "single" && (
        <div className="mt-4 space-y-2">
          {q.options.map((opt) => {
            const on = value === opt;
            return (
              <button key={opt} type="button" onClick={() => onChange(opt)}
                data-testid={`dd-opt-${q.id}-${opt.slice(0, 30).replace(/\W+/g, "-").toLowerCase()}`}
                className={`w-full text-left text-sm px-4 py-3 rounded-xl border transition ${on ? "border-[#5C1E8A] bg-purple-50" : "border-slate-200 hover:border-purple-400"}`}>
                {opt}
              </button>
            );
          })}
        </div>
      )}
      {q.type === "multi" && (
        <div className="mt-4 grid sm:grid-cols-2 gap-2">
          {q.options.map((opt) => {
            const list = Array.isArray(value) ? value : [];
            const on = list.includes(opt);
            const toggle = () => onChange(on ? list.filter((x) => x !== opt) : [...list, opt]);
            return (
              <button key={opt} type="button" onClick={toggle}
                data-testid={`dd-multi-${q.id}-${opt.slice(0, 30).replace(/\W+/g, "-").toLowerCase()}`}
                className={`text-left text-sm px-3 py-2.5 rounded-lg border transition ${on ? "border-[#5C1E8A] bg-purple-50" : "border-slate-200 hover:border-purple-400"}`}>
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ─────────────────── Completion (with Preliminary Summary) ─────────────────── */
function Complete({ tier, purchaseToken, completionData }) {
  const t = TIER_LABELS[tier] || TIER_LABELS.intro;
  const initialSummary = completionData?.summary || null;
  const shouldFetch = !initialSummary && Boolean(purchaseToken);
  const [summary, setSummary] = useState(initialSummary);
  const [loading, setLoading] = useState(shouldFetch);

  useEffect(() => {
    if (!shouldFetch) return undefined;
    let cancelled = false;
    deepDiveApi.summary(purchaseToken)
      .then((r) => { if (!cancelled) { setSummary(r?.data?.summary || null); setLoading(false); } })
      .catch(() => { if (!cancelled) { setSummary(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [shouldFetch, purchaseToken]);

  const turnaround = tier === "premium" ? "within 3 business days"
                    : tier === "standard" ? "within 5 business days"
                    : "within 7 business days";

  return (
    <Shell testid="deep-dive-complete" tier={tier}>
      {/* Concierge dark hero */}
      <section className="relative bg-[#150B27] text-white overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 left-1/4 w-[28rem] h-[28rem] rounded-full bg-emerald-700/20 blur-3xl" />
          <div className="absolute top-10 right-0 w-96 h-96 rounded-full bg-purple-700/30 blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 pt-20 pb-20 text-center">
          <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-[#2D6B50] shadow-lg" data-testid="dd-complete-check">
            <svg viewBox="0 0 24 24" className="w-9 h-9 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          </div>
          <span className="block mt-5 text-[10px] uppercase tracking-[0.32em] text-purple-200 font-semibold">{t.label}</span>
          <h1
            className="mt-3 text-4xl sm:text-5xl"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
            data-testid="dd-complete-headline"
          >
            Thank you for sharing your story.
          </h1>
          <p className="mt-5 text-base text-slate-200 max-w-2xl mx-auto leading-relaxed">
            Your KCIS clinical team has received your Deep Dive responses and will deliver your full personalized dossier <strong className="text-white">{turnaround}</strong>.
          </p>
        </div>
      </section>

      {/* Preliminary Summary card */}
      <main className="max-w-3xl mx-auto px-6 py-14">
        <PreliminarySummaryCard tier={tier} loading={loading} summary={summary} />

        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.32em] text-purple-900 font-semibold mb-2">What happens next</div>
          <ol className="space-y-2 text-sm text-slate-800 leading-relaxed list-decimal pl-5">
            <li>Our clinical team reviews every word you wrote and prepares your tier-specific dossier.</li>
            <li>Your KCIS findings, recommendations, and next-step guidance will be delivered <strong>{turnaround}</strong> to the email on file.</li>
            <li>Once delivered, you&rsquo;ll be invited to walk through your findings together.</li>
          </ol>
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <a href={BOOKING_URL} rel="noopener noreferrer" data-testid="dd-book-wellness">
            <Button size="lg" className="w-full rounded-full bg-[#2D6B50] hover:bg-[#22573F] text-white py-6">
              <CalendarCheck2 className="w-4 h-4 mr-2" /> Book Wellness Analysis
            </Button>
          </a>
          <a href={MICROSCOPIC_URL} rel="noopener noreferrer" data-testid="dd-book-microscopic">
            <Button size="lg" variant="outline" className="w-full rounded-full border-[#5C1E8A] text-[#5C1E8A] hover:bg-purple-50 py-6">
              <Microscope className="w-4 h-4 mr-2" /> Microscopic Hair &amp; Scalp
            </Button>
          </a>
          <a href={CONSULTATION_URL} rel="noopener noreferrer" data-testid="dd-book-consultation">
            <Button size="lg" variant="outline" className="w-full rounded-full border-[#5C1E8A] text-[#5C1E8A] hover:bg-purple-50 py-6">
              <Stethoscope className="w-4 h-4 mr-2" /> Schedule Consultation
            </Button>
          </a>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-700 leading-relaxed flex items-start gap-3">
          <Mail className="w-4 h-4 text-[#5C1E8A] mt-0.5 shrink-0" />
          <span>You&rsquo;ll receive a confirmation email shortly with this summary attached. If you don&rsquo;t see it, please check spam, then email <a className="text-[#5C1E8A] underline" href="mailto:c.von@kissedbycvon.com">c.von@kissedbycvon.com</a>.</span>
        </div>

        <p className="mt-12 text-center text-xs text-slate-500 italic">
          &ldquo;We kiss the mind, teach the wisdom, and make the mission better.&rdquo;
        </p>
      </main>
    </Shell>
  );
}


function PreliminarySummaryCard({ tier, loading, summary }) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-purple-200 bg-white p-8 shadow-sm text-center" data-testid="dd-summary-loading">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-purple-900" />
        <p className="mt-4 text-sm text-slate-700">Preparing your preliminary intelligence summary…</p>
      </div>
    );
  }
  if (!summary) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="dd-summary-fallback">
        <div className="text-[10px] uppercase tracking-[0.32em] text-purple-900 font-semibold mb-2">Preliminary Intelligence Summary</div>
        <p className="text-sm text-slate-700 leading-relaxed">Your responses are being reviewed manually by the KISSED clinical team. Your dossier will arrive on your email shortly.</p>
      </div>
    );
  }

  const reasonNote = summary.reason_note;
  const buckets = Array.isArray(summary.available_buckets) ? summary.available_buckets : [];
  const has = (key) => buckets.includes(key);

  // Engine-empty fallback (every visible bucket came back empty).
  const everyVisibleBucketEmpty =
    (!has("top_symptom_patterns")       || (summary.top_symptom_patterns       || []).length === 0) &&
    (!has("top_deficiency_patterns")    || (summary.top_deficiency_patterns    || []).length === 0) &&
    (!has("top_condition_patterns")     || (summary.top_condition_patterns     || []).length === 0) &&
    (!has("top_herb_categories")        || (summary.top_herb_categories        || []).length === 0) &&
    (!has("top_formulation_categories") || (summary.top_formulation_categories || []).length === 0) &&
    (!has("safety_considerations")      || (summary.safety_considerations      || []).length === 0);

  return (
    <div className="rounded-3xl border border-purple-200 bg-gradient-to-br from-white via-white to-purple-50/40 p-7 sm:p-9 shadow-sm" data-testid="dd-summary-card">
      <div className="text-[10px] uppercase tracking-[0.32em] text-purple-900 font-semibold">
        {summary.headline || "Preliminary Clinical Intelligence Summary"}
      </div>
      <h2
        className="mt-2 text-3xl sm:text-4xl text-slate-900 leading-tight"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
      >
        Your story is pointing toward a few clear patterns.
      </h2>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">
        This is <strong>not</strong> a diagnosis or treatment plan. It is directional pattern recognition that your KISSED clinical team will refine in your personalized dossier.
      </p>

      {reasonNote ? (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-900" data-testid="dd-summary-note">
          {reasonNote}
        </div>
      ) : null}

      {has("safety_considerations") && (summary.safety_considerations || []).length > 0 && (
        <SafetyOverview items={summary.safety_considerations} />
      )}

      {everyVisibleBucketEmpty ? (
        <div className="mt-7 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-700 leading-relaxed" data-testid="dd-summary-all-empty">
          Your responses are rich in narrative detail. Your KISSED clinical team will review them personally and deliver your full dossier shortly.
        </div>
      ) : (
        <div className="mt-7 space-y-6">
          {has("top_symptom_patterns") && (summary.top_symptom_patterns || []).length > 0 && (
            <SummaryBucket title="Top Symptom Patterns" items={summary.top_symptom_patterns} testid="bucket-symptoms" />
          )}
          {has("top_deficiency_patterns") && (summary.top_deficiency_patterns || []).length > 0 && (
            <SummaryBucket title="Top Deficiency Patterns" items={summary.top_deficiency_patterns} showConfidence testid="bucket-deficiencies" />
          )}
          {has("top_condition_patterns") && (summary.top_condition_patterns || []).length > 0 && (
            <SummaryBucket title="Top Condition Patterns" items={summary.top_condition_patterns} showConfidence testid="bucket-conditions" />
          )}
          {has("top_herb_categories") && (summary.top_herb_categories || []).length > 0 && (
            <SummaryBucket title="Top Herbal Categories" items={summary.top_herb_categories} showExamples testid="bucket-herbs" />
          )}
          {has("top_formulation_categories") && (summary.top_formulation_categories || []).length > 0 && (
            <SummaryBucket title="Top Formulation Categories" items={summary.top_formulation_categories} showExamples testid="bucket-formulations" />
          )}
          {has("safety_considerations") && (summary.safety_considerations || []).length > 0 && (
            <SafetyBucket items={summary.safety_considerations} />
          )}
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5">
        <div className="text-[10px] uppercase tracking-[0.32em] text-emerald-800 font-semibold">Recommended Next Step</div>
        <p className="mt-2 text-sm text-slate-800 leading-relaxed" data-testid="dd-summary-next-step">
          {summary.recommended_next_step}
        </p>
      </div>
    </div>
  );
}


function SummaryBucket({ title, items, showConfidence = false, showExamples = false, testid }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div data-testid={`dd-summary-${testid}`}>
      <div className="text-[10px] uppercase tracking-[0.28em] text-purple-900 font-semibold mb-2">{title}</div>
      <ul className="space-y-1.5">
        {items.map((it, idx) => (
          <li key={`${it.slug || it.name}-${idx}`} className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800">
            <div>
              <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }} className="text-base text-slate-900">{it.name}</span>
              {showExamples && Array.isArray(it.top_examples) && it.top_examples.length > 0 ? (
                <div className="text-xs text-slate-500 mt-0.5">e.g., {it.top_examples.join(", ")}</div>
              ) : null}
            </div>
            {showConfidence && typeof it.confidence === "number" ? (
              <span className="text-[10px] uppercase tracking-[0.22em] text-purple-900 font-semibold whitespace-nowrap">
                signal {Math.round(it.confidence * 100)}%
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}


function SafetyOverview({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const counts = items.reduce((acc, a) => {
    const sev = (a?.severity || "info").toLowerCase();
    acc[sev] = (acc[sev] || 0) + 1;
    return acc;
  }, {});
  const order = [
    { key: "block",   label: "CRITICAL", color: "bg-red-600",      text: "text-white", ring: "ring-red-200",      border: "border-red-300" },
    { key: "warning", label: "WARNING",  color: "bg-amber-500",    text: "text-white", ring: "ring-amber-200",    border: "border-amber-300" },
    { key: "caution", label: "CAUTION",  color: "bg-purple-700",   text: "text-white", ring: "ring-purple-200",   border: "border-purple-300" },
  ];
  const visible = order.filter((b) => (counts[b.key] || 0) > 0);
  if (visible.length === 0) return null;

  const scrollToSafety = () => {
    const el = document.querySelector('[data-testid="dd-summary-bucket-safety"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="dd-summary-safety-overview"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-[10px] uppercase tracking-[0.28em] text-purple-900 font-semibold">
          Safety Overview
        </div>
        <button
          type="button"
          onClick={scrollToSafety}
          className="text-[11px] uppercase tracking-[0.22em] text-purple-900 font-semibold underline-offset-2 hover:underline"
          data-testid="dd-safety-overview-jump"
        >
          Review below ↓
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {visible.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={scrollToSafety}
            className={`text-left rounded-xl border ${b.border} bg-white px-4 py-3 hover:ring-4 hover:${b.ring} transition`}
            data-testid={`dd-safety-overview-${b.key}`}
          >
            <span className={`inline-block ${b.color} ${b.text} text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded-full`}>
              {b.label}
            </span>
            <div
              className="mt-2 text-3xl text-slate-900"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
            >
              {counts[b.key]}
            </div>
            <div className="text-xs text-slate-500">
              {counts[b.key] === 1 ? "finding" : "findings"}
            </div>
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500 leading-relaxed">
        Tap any tile to jump to the detailed safety findings below. Your KISSED clinical team reviews every alert during your dossier preparation.
      </p>
    </div>
  );
}


function SafetyBucket({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const colorForSeverity = (s) => ({
    info: "border-purple-200 bg-purple-50",
    caution: "border-amber-200 bg-amber-50",
    warning: "border-red-200 bg-red-50",
    block: "border-red-300 bg-red-100",
  })[(s || "info").toLowerCase()] || "border-slate-200 bg-white";

  return (
    <div data-testid="dd-summary-bucket-safety">
      <div className="text-[10px] uppercase tracking-[0.28em] text-purple-900 font-semibold mb-2">Safety Considerations</div>
      <ul className="space-y-2">
        {items.map((a, idx) => (
          <li key={idx} className={`rounded-xl border px-4 py-3 text-sm ${colorForSeverity(a.severity)}`}>
            <div className="flex items-center justify-between gap-3">
              <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }} className="text-base text-slate-900">{a.flag_name || a.scope}</span>
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">{a.severity}</span>
            </div>
            {a.message ? <p className="mt-1 text-xs text-slate-700 leading-relaxed">{a.message}</p> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}


/* ─────────────────── Shared shells ─────────────────── */
function Shell({ children, testid, tier }) {
  return (
    <div className="min-h-screen bg-[#FAF7FB]" data-testid={testid} data-tier={tier}>
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
            <span className="font-display text-sm text-slate-700">KCIS Clinical Deep Dive™</span>
          </Link>
        </div>
      </header>
      {children}
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7FB]">
      <div className="text-center text-slate-600">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-purple-900" />
        <p className="mt-3 text-sm">Preparing your Deep Dive…</p>
      </div>
    </div>
  );
}

function ErrorShell({ msg }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7FB] px-6">
      <div className="max-w-md rounded-2xl border border-red-200 bg-white p-6 text-center">
        <h2 className="font-display text-xl text-red-800">We hit a snag.</h2>
        <p className="mt-2 text-sm text-red-700">{msg}</p>
        <p className="mt-4 text-xs text-slate-500">Email <a href="mailto:c.von@kissedbycvon.com" className="text-purple-900">c.von@kissedbycvon.com</a> and we&rsquo;ll sort it out.</p>
      </div>
    </div>
  );
}
